import { API_ENDPOINTS, API_URL } from '@/constants/api';
import { readJson } from '@/shared/utils/json';
import { useCallback, useEffect, useState } from 'react';

export const SHIPPING_OPTION_VALUES = ['DPD', 'Amazon'] as const;
export type ShippingOptionValue = (typeof SHIPPING_OPTION_VALUES)[number];

export type SubscriptionContract = {
  status: string;
  nextBillingDate: string | null;
  lastPaymentStatus: string | null;
  lines: Array<{
    id: string;
    quantity: number;
    variantId: string;
    title: string;
    sku: string;
    currentPrice: {
      amount: string;
    };
    lineDiscountedPrice: {
      amount: string;
    };
  }>;
  discounts: Array<{
    id: string;
    title: string;
    value: {
      amount?: {
        amount: string;
      };
      percentage?: number;
    };
  }>;
  customer: {
    email: string;
    firstName: string;
    lastName: string;
    defaultPhoneNumber?: {
      phoneNumber: string;
    };
    defaultAddress?: {
      phone: string;
    };
    addressesV2?: Array<{
      phone: string;
    }>;
  };
  customerPaymentMethod: {
    id: string | null;
    revokedAt: string | null;
    instrument: {
      brand?: string;
      lastDigits?: string;
      paypalAccountEmail?: string;
    };
  };
  deliveryMethod: {
    address: {
      firstName: string;
      lastName: string;
      phone: string | null;
      address1: string;
      address2: string | null;
      city: string;
      countryCode: string;
      zip: string;
    };
    localDeliveryOption?: {
      code: string | null;
      title: string;
    };
    shippingOption?: {
      code: string | null;
      title: string;
    };
  };
};

/**
 * Maps Shopify subscription delivery codes to Nero options.
 */
export function getContractShippingCode(contract: SubscriptionContract): ShippingOptionValue {
  const code =
    contract.deliveryMethod?.shippingOption?.code ??
    contract.deliveryMethod?.localDeliveryOption?.code ??
    null;
  if (code === 'Amazon Tracked Delivery') {
    return 'Amazon';
  }
  return 'DPD';
}

/** Customer-facing label for the current contract row (matches Purrscribe/Shopify titles after update). */
export function getContractShippingDisplayTitle(contract: SubscriptionContract): string {
  const title =
    contract.deliveryMethod?.shippingOption?.title ??
    contract.deliveryMethod?.localDeliveryOption?.title ??
    null;

  return title ?? 'DPD Tracked Delivery';
}

export const useSubscriptionContract = (shopifyCustomerId: string) => {
  const [contract, setContract] = useState<SubscriptionContract | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shippingUpdateLoading, setShippingUpdateLoading] = useState(false);
  const [shippingUpdateError, setShippingUpdateError] = useState<string | null>(null);

  const fetchContract = useCallback(
    async (options?: { skipLoadingSpinner?: boolean }) => {
      if (!shopifyCustomerId) return;

      await Promise.resolve();

      if (!options?.skipLoadingSpinner) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await fetch(
          `${API_URL}${API_ENDPOINTS.CUSTOMERS.SUBSCRIPTION_CONTRACT(shopifyCustomerId)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError('Subscription contract not found');
            if (!options?.skipLoadingSpinner) {
              setContract(null);
            }
            return;
          }
          throw new Error('Failed to fetch subscription contract');
        }

        const data = await readJson<SubscriptionContract>(response);
        setContract(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        if (!options?.skipLoadingSpinner) {
          setContract(null);
        }
      } finally {
        if (!options?.skipLoadingSpinner) {
          setLoading(false);
        }
      }
    },
    [shopifyCustomerId],
  );

  useEffect(() => {
    void Promise.resolve().then(() => fetchContract());
  }, [fetchContract]);

  /**
   * PATCHes the customer’s subscription contract shipping option and reloads contract data on success.
   */
  const updateShippingOption = useCallback(
    async (shippingOption: ShippingOptionValue) => {
      if (!shopifyCustomerId) return;

      setShippingUpdateLoading(true);
      setShippingUpdateError(null);

      try {
        const response = await fetch(
          `${API_URL}${API_ENDPOINTS.CUSTOMERS.SHIPPING_OPTION(shopifyCustomerId)}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ shippingOption }),
          },
        );

        if (!response.ok) {
          const responseText = await response.text();
          let errorMessage = 'Failed to update shipping option';
          if (responseText) {
            try {
              const errorData = JSON.parse(responseText) as { message?: string; status?: string };
              errorMessage = errorData.message || errorData.status || errorMessage;
            } catch {
              errorMessage = responseText || errorMessage;
            }
          }
          throw new Error(errorMessage);
        }

        await fetchContract({ skipLoadingSpinner: true });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setShippingUpdateError(message);
      } finally {
        setShippingUpdateLoading(false);
      }
    },
    [shopifyCustomerId, fetchContract],
  );

  const clearShippingUpdateError = useCallback(() => setShippingUpdateError(null), []);

  return {
    contract,
    loading,
    error,
    updateShippingOption,
    shippingUpdateLoading,
    shippingUpdateError,
    clearShippingUpdateError,
  };
};
